import React from 'react'
import { Send } from 'lucide-react'
import { BrandingFormState } from './BrandingEditor'

interface BrandingPreviewProps {
  branding: BrandingFormState
  operatorName: string
}

// --- Initials Avatar (fallback when no logo) ---

const InitialsAvatar: React.FC<{ name: string; color: string }> = ({ name, color }) => {
  const initials = name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
      style={{ backgroundColor: color }}
    >
      {initials || '?'}
    </div>
  )
}

// --- Sample Conversation Data ---

const SAMPLE_MESSAGES = [
  { type: 'assistant' as const, text: 'Welcome! How can I help you today?' },
  { type: 'user' as const, text: "I'd like to book a room for 2 guests" },
  { type: 'assistant' as const, text: 'I found 3 options for you. Here are the best matches:' },
]

// --- Helpers ---

/** Converts hex to rgba for tinted backgrounds */
function hexToRgba(hex: string, alpha: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return `rgba(0,0,0,${alpha})`
  const r = parseInt(result[1], 16)
  const g = parseInt(result[2], 16)
  const b = parseInt(result[3], 16)
  return `rgba(${r},${g},${b},${alpha})`
}

// --- Component ---

const BrandingPreview: React.FC<BrandingPreviewProps> = ({ branding, operatorName }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Live Preview</h2>

      {/* Mini widget container */}
      <div
        className="rounded-lg shadow-lg border border-gray-200 overflow-hidden mx-auto"
        style={{
          maxWidth: '320px',
          backgroundColor: branding.backgroundColor,
        }}
      >
        {/* Header bar */}
        <div
          className="px-4 py-3 flex items-center gap-3"
          style={{ backgroundColor: branding.primaryColor }}
        >
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={operatorName}
              className="w-8 h-8 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <InitialsAvatar name={operatorName} color={branding.primaryColor} />
          )}
          <span
            className="text-sm font-semibold truncate"
            style={{ color: '#ffffff' }}
          >
            {operatorName || 'Your Company'}
          </span>
        </div>

        {/* Chat area */}
        <div
          className="px-3 py-4 space-y-3"
          style={{ minHeight: '220px' }}
        >
          {SAMPLE_MESSAGES.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className="rounded-lg px-3 py-2 text-xs leading-relaxed max-w-[85%]"
                style={
                  msg.type === 'user'
                    ? {
                        backgroundColor: branding.primaryColor,
                        color: '#ffffff',
                      }
                    : {
                        backgroundColor: hexToRgba(branding.secondaryColor, 0.1),
                        color: branding.textColor,
                      }
                }
              >
                {msg.text}
              </div>
            </div>
          ))}
        </div>

        {/* Input area */}
        <div className="px-3 pb-3">
          <div
            className="flex items-center gap-2 rounded-lg border px-3 py-2"
            style={{ borderColor: hexToRgba(branding.textColor, 0.15) }}
          >
            <span
              className="text-xs flex-1"
              style={{ color: hexToRgba(branding.textColor, 0.4) }}
            >
              Type a message...
            </span>
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: branding.accentColor }}
            >
              <Send className="w-3 h-3 text-white" />
            </div>
          </div>
        </div>

        {/* Powered by footer */}
        <div
          className="text-center py-2 border-t"
          style={{
            borderColor: hexToRgba(branding.textColor, 0.08),
          }}
        >
          <span
            className="text-[10px]"
            style={{ color: hexToRgba(branding.textColor, 0.35) }}
          >
            Powered by AI Booking Engine
          </span>
        </div>
      </div>
    </div>
  )
}

export default BrandingPreview
